import { DocumentIcon } from '@heroicons/react/24/outline';
import type { DocumentPagesFilters as DocumentPagesFiltersType } from '../components/DocumentPagesFilters';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import type { DocumentPageSchema } from '@avisoma/shared';
import { Link } from '../components/Link';
import FileSize from '../components/FileSize';
import backend from '../services/backend';
import { ClassificationCell, ProcessingStatusCells, TagsCell } from '../components/tableCells';
import { SuggestedCategory } from '../components/SuggestedCategory';
import { PageHeader } from '../components/PageHeader';
import SortableTable, { type SortableTableColumn } from '../components/SortableTable';
import ListWrapper from '../components/ListWrapper';
import DocumentPagesFilters from '../components/DocumentPagesFilters';
import { LazyImage } from '../components/LazyImage';
import { FullScreenImagePreviewTrigger } from '../components/FullScreenImagePreview';
import { PagePreviewHoverProvider } from '../components/pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from '../components/pagePreview/PagePreviewHoverCard';
import { InfinityScroll } from '../components/InfinityScroll';
import { BulkSidebarShell, DangerZoneCard } from '../components/bulkSidebar';
import { ActionableButton } from '../components/ActionableButton';
import { Radio, RadioField, RadioGroup } from '../catalyst/radio';
import { Label } from '../catalyst/fieldset';
import pQueue from 'p-queue';
import { useAuth } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';
import { ID } from '../components/ID';
import { formatDateOnly } from '../lib/dateFormat';
import { useInfiniteList } from '../hooks/useInfiniteList';

const ITEMS_PER_PAGE = 100;

export default function DocumentPages() {
  useDocumentTitle('Document Pages');
  const [filters, setFilters] = useState<DocumentPagesFiltersType>();
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedRows, setSelectedRows] = useState(new Set<string>());
  const { hasAccess } = useAuth();

  const queryKey = useMemo(() => ['documentPages', filters], [filters]);

  const handleFilterChange = (newFilters: DocumentPagesFiltersType) => {
    setFilters(newFilters);
    setSelectedRows(new Set());
  };

  const {
    items: documentPages,
    hasMore: hasNextPage,
    loading: isLoadingMore,
    loadMore,
  } = useInfiniteList<DocumentPageSchema>({
    queryKey,
    queryFn: (limit) =>
      backend
        .listDocumentPages(1, limit, filters!)
        .then((res) => ({ items: res.data.items, hasMore: res.data.hasNext })),
    enabled: !!filters,
    initialItemsPerPage: ITEMS_PER_PAGE,
    resetKey: filters,
  });

  const columns = useMemo((): SortableTableColumn<DocumentPageSchema>[] => {
    return [
      {
        accessor: 'fileName',
        label: 'File Name',
        render: (doc) => (
          <Link
            to={`/document-pages/${doc.id}`}
            className="text-ellipsis overflow-hidden max-w-[250px] inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            <ID id={doc.id} />
          </Link>
        ),
      },
      {
        accessor: 'category',
        label: 'Category',
        render: (doc) => doc.category ?? '—',
      },
      {
        accessor: 'suggestedCategory',
        label: 'Suggested Category',
        render: (doc) => <ClassificationCell category={doc.category} processing={doc.processing} />,
      },
      {
        accessor: 'status',
        label: 'Status',
        render: (doc) => (
          <ProcessingStatusCells
            processing={doc.processing}
            steps={['segmentation', 'pdfParser', 'metadata', 'embeddings', 'classification', 'dataExtraction']}
          />
        ),
      },
      {
        accessor: 'tags',
        label: 'Tags',
        render: (doc) => <TagsCell tags={doc.tags} />,
      },
      {
        accessor: 'size',
        label: 'Size',
        render: (doc) => <FileSize bytes={doc.size} />,
      },
      {
        accessor: 'createdAt',
        label: 'Created',
        render: (doc) => formatDateOnly(doc.createdAt),
      },
    ];
  }, []);

  return (
    <div className="">
      {/* Header */}
      <PageHeader
        title="Document Pages"
        description="Manage and organize your document pages"
        icon={<DocumentIcon className="size-6" />}
      >
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-500">
            {/* {lastFetched && (
                <>Last updated: {lastFetched.toLocaleTimeString()}</>
              )} */}
          </p>
        </div>
      </PageHeader>

      {/* Filters */}
      <DocumentPagesFilters onFilterChange={handleFilterChange} onViewModeChange={setViewMode} />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Documents Table */}
        <div className={`${selectedRows.size === 0 ? 'lg:col-span-12' : 'lg:col-span-8'}`}>
          <ListWrapper items={documentPages}>
            <div className="overflow-hidden rounded-lg">
              {viewMode === 'list' && (
                <>
                  <SortableTable
                    storageId="document-pages"
                    limitHeight={false}
                    columns={columns}
                    data={documentPages}
                    rowKey={(row) => row.id}
                    setSelectedRows={hasAccess('admin') ? setSelectedRows : undefined}
                    selectedRows={selectedRows}
                  />
                  <InfinityScroll
                    onLoadMore={() => loadMore()}
                    rootMargin="400px"
                    enabled={hasNextPage && !isLoadingMore && !!filters}
                  />
                </>
              )}

              {viewMode === 'grid' && (
                <>
                  <PagePreviewHoverProvider visibleIds={new Set(documentPages.map((d) => d.id))} dismissKey={filters}>
                  <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {documentPages.map((doc) => (
                      <PagePreviewHoverCard
                        key={doc.id}
                        page={{ id: doc.id, classification: doc.processing?.classification?.result }}
                        className="flex flex-col gap-2"
                      >
                        <div className="relative group bg-gray-200 p-1.5 dark:bg-zinc-700 aspect-3/4 rounded-lg overflow-hidden ">
                          {/* <LazyIframe src={pdfUrl} className="w-full h-full" /> */}
                          <LazyImage
                            src={pagePreviewSrc(doc.id, { resize: 'x450' })}
                            className="w-full h-full rounded-md overflow-hidden"
                          />

                          <FullScreenImagePreviewTrigger pageId={doc.id} />
                        </div>

                        <Link to={`/document-pages/${doc.id}`} className="text-sm text-gray-500">
                          <SuggestedCategory
                            documentCategory={doc.category}
                            suggestedCategory={doc.processing?.classification?.result?.[0]}
                          />
                        </Link>
                      </PagePreviewHoverCard>
                    ))}
                  </div>
                  </PagePreviewHoverProvider>
                  <InfinityScroll
                    onLoadMore={() => loadMore()}
                    rootMargin="400px"
                    enabled={hasNextPage && !isLoadingMore && !!filters}
                  />
                </>
              )}
            </div>
          </ListWrapper>
        </div>

        {/* Tag Management Section */}
        <DocumentPageActions selectedRows={selectedRows} setSelectedRows={setSelectedRows} />
      </div>
    </div>
  );
}

type ResetDepth = 'classification' | 'pdf-parser';

const resetDepths: Array<{ label: string; value: ResetDepth }> = [
  {
    label: 'Depth 1: Reset classification only',
    value: 'classification',
  },
  {
    label: 'Depth 4: Reset pdfPraser',
    value: 'pdf-parser',
  },
];

function DocumentPageActions({
  selectedRows,
  setSelectedRows,
}: {
  selectedRows: Set<string>;
  setSelectedRows: Dispatch<SetStateAction<Set<string>>>;
}) {
  const [isDangerZoneExpanded, setIsDangerZoneExpanded] = useState(true);
  const [resetDepth, setResetDepth] = useState<ResetDepth>('classification');
  const [resetProgress, setResetProgress] = useState<number>(0);

  const handleResetSelected = async () => {
    if (!resetDepth) return;
    const queue = new pQueue({ concurrency: 10 });
    setResetProgress(0);
    await Promise.all(
      Array.from(selectedRows).map(async (id) => {
        await queue.add(async () => {
          await backend.pageRetry(id, resetDepth).then(() => {
            setResetProgress((prev) => prev + 1);
          });
        });
      }),
    );
  };

  if (selectedRows.size === 0) return null;

  return (
    <BulkSidebarShell selectedCount={selectedRows.size} onDeselectAll={() => setSelectedRows(new Set())}>
      <DangerZoneCard
        expanded={isDangerZoneExpanded}
        onToggle={() => setIsDangerZoneExpanded(!isDangerZoneExpanded)}
      >
        <RadioGroup value={resetDepth} onChange={(value) => setResetDepth(value as ResetDepth)}>
          {resetDepths.map((depth) => (
            <RadioField key={depth.value}>
              <Radio value={depth.value} />
              <Label>{depth.label}</Label>
            </RadioField>
          ))}
        </RadioGroup>
        <ActionableButton
          onClick={handleResetSelected}
          color="amber"
          className="w-full"
          loader={`Resetting ${((resetProgress / selectedRows.size) * 100).toFixed(0)}%...`}
        >
          Reset Selected Documents
        </ActionableButton>
      </DangerZoneCard>
    </BulkSidebarShell>
  );
}
